import { useQuery } from "@oysterhr/core"
import { gql } from "../gql"
import { OysterIntelligenceCustomerGetPlansAndPermissionsQuery } from "../gql/graphql"

export const OYSTER_INTELLIGENCE_CUSTOMER_GET_PLANS_AND_PERMISSIONS = gql(`
  query OysterIntelligenceCustomerGetPlansAndPermissions($companyId: ID!) {
    company(companyId: $companyId) {
      id
      paymentMethod {
        ... on PaymentMethodStripeAch {
          id
        }
        ... on PaymentMethodStripeBacs {
          id
        }
        ... on PaymentMethodStripeSepa {
          id
        }
        ... on PaymentMethodStripePad {
          id
        }
      }
      pendingAddOns(page: 1, perPage: 50) {
        collection {
            id
            name
            isTrial
            startsOn
          }
        }
      activeAddOns(page: 1, perPage: 50) {
        collection {
            id
            name
            isTrial
            startsOn
            trialDaysRemaining
          }
        }
      expiredAddOns(page: 1, perPage: 50) {
        collection {
            id
            name
            isTrial
          }
        }
      availableAddOnPlans(page: 1, perPage: 50) {
        collection {
          id
          name
          description
          isTrial
          planPrice(companyId: $companyId) {
            amount {
              subunitToUnit
              subunit
              currencyCode
            }
            id
          }
          billingFrequency
          product
          supportedType
        }
      }
      authorizations {
        actions
      }
    }
  }
`)

export function useSalaryInsightsPlansAndPermissions(companyId: string | null) {
  const { data, loading } = useQuery(OYSTER_INTELLIGENCE_CUSTOMER_GET_PLANS_AND_PERMISSIONS, {
    variables: {
      companyId: companyId as string,
    },
    fetchPolicy: "network-only",
    skip: !companyId,
  })

  const hasPermissionToSubscribe = data?.company?.authorizations?.actions?.includes(
    "purchase_salary_insights",
  )

  const pendingSalaryInsightsTrialAddOn = data?.company?.pendingAddOns?.collection?.find(
    plan => plan.name === "Salary Insights Trial" && plan.isTrial === true,
  )

  const activeSalaryInsightsTrialAddOn = data?.company?.activeAddOns?.collection?.find(
    plan => plan.name === "Salary Insights Trial" && plan.isTrial === true,
  )

  const expiredSalaryInsightsTrialAddOn = data?.company?.expiredAddOns?.collection?.find(
    plan => plan.name === "Salary Insights Trial" && plan.isTrial === true,
  )

  const salaryInsightsTrialAvailable = data?.company?.availableAddOnPlans?.collection?.find(
    plan => plan.name === "Salary Insights Trial" && plan.isTrial === true,
  )

  function getSalaryInsightsPlan() {
    const paidPlan = data?.company?.availableAddOnPlans?.collection?.find(
      plan => plan.product === "salary_insights" && plan.isTrial === false,
    )

    const trial = data?.company?.availableAddOnPlans?.collection?.find(
      plan => plan.product === "salary_insights_trial" && plan.isTrial === true,
    )

    if (trial) {
      return trial
    }

    return paidPlan
  }

  const hasPaymentMethod = !!data?.company?.paymentMethod
  const accessActions = ["access_salary_insights", "access_salary_insights_trial"]
  const companyActions = data?.company?.authorizations?.actions || []
  const hasAccessToSalaryInsights = accessActions.some(action => companyActions?.includes(action))

  return {
    hasAccessToSalaryInsights,
    hasPermissionToSubscribe,
    hasPaymentMethod,
    getSalaryInsightsPlan,
    pendingSalaryInsightsTrialAddOn,
    activeSalaryInsightsTrialAddOn,
    expiredSalaryInsightsTrialAddOn,
    salaryInsightsTrialAvailable,
    loading,
  }
}

export type SalaryInsightsPlanType = NonNullable<
  NonNullable<
    OysterIntelligenceCustomerGetPlansAndPermissionsQuery["company"]
  >["availableAddOnPlans"]
>["collection"][number]

export type SalaryInsightsPendingAddOnType = NonNullable<
  NonNullable<OysterIntelligenceCustomerGetPlansAndPermissionsQuery["company"]>["pendingAddOns"]
>["collection"][number]

export type SalaryInsightsExpiredAddOnType = NonNullable<
  NonNullable<OysterIntelligenceCustomerGetPlansAndPermissionsQuery["company"]>["expiredAddOns"]
>["collection"][number]
